import { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const SIZE_LIMITS = {
  image: 10 * 1024 * 1024,  // 10MB
  audio: 20 * 1024 * 1024,  // 20MB
  video: 200 * 1024 * 1024  // 200MB
};

export function useAssetUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const uploadAsset = async (kind, file) => {
    if (!file) {
      toast.error('No file selected');
      return null;
    }

    // Validate kind
    if (!['image', 'audio', 'video'].includes(kind)) {
      toast.error('Invalid file type');
      return null;
    }

    // Validate size
    if (file.size > SIZE_LIMITS[kind]) {
      const limitMB = SIZE_LIMITS[kind] / (1024 * 1024);
      toast.error(`File too large (limit ${limitMB}MB)`);
      return null;
    }

    // Validate file type
    const validTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      audio: ['audio/mpeg', 'audio/mp3', 'audio/webm', 'audio/ogg', 'audio/wav', 'audio/mp4', 'audio/x-m4a'],
      video: ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo']
    };

    if (!validTypes[kind].some(type => file.type.startsWith(type.split('/')[0]))) {
      toast.error(`Invalid ${kind} file type`);
      return null;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', kind);

      const response = await api.post('/assets/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percent);
        }
      });

      setProgress(100);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} uploaded successfully`);

      return response.data;
    } catch (error) {
      console.error(`${kind} upload failed:`, error);
      
      // Extract error message from response
      let errorMessage = `Failed to upload ${kind}`;
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setUploading(false);
      setTimeout(() => {
        setProgress(0);
        setError(null);
      }, 2000);
    }
  };

  return {
    uploadAsset,
    uploading,
    progress,
    error
  };
}
